// A compact copy of the network for the Worker's MCP endpoint (worker/mcp.ts).
//
// The full network is several megabytes of JSON, and parsing it costs more CPU than one request on the Workers
// free plan allows. This copy drops what the MCP tools never read (touch history, notes, LinkedIn URLs) and
// stores the rest column by column: small dictionaries for repeated values and one character per sector. The
// browser builds it next to the full network (client/sources/build.ts). Pure: no DOM, no Node APIs.

import { askedSectorsFromTouches } from "./categorize";
import { saidNoTo } from "./lists-engine";
import { SECTORS, type Network } from "./network";

/** The contact fields that survive slimming (a subset of network.ts's Contact, same names). */
export interface SlimContact {
  k: string;
  id: string;
  n: string;
  co: string;
  ti: string;
  ty: string;
  ci: string;
  st: string;
  rg: string;
  se: string[];
  cmin: number;
  cmax: number;
  cs: string;
  rs: number;
  em: string;
  ph: string;
  hm: string;
  src: string[];
  fl: { flag: string; date: string | null }[];
  pa: { deal: string; outcome: string }[];
  bu: string[];
  ji: { sector: string; date: string | null } | null;
  as: { sector: string; date: string | null }[];
  np: string[];
  /** Last touch either way, ISO date, or "" when there is none. */
  lt: string;
}

export interface SlimNetwork {
  v: 1;
  generated: string;
  epoch: string;
  count: number;
  /** The sector list the sector codes below index into. */
  sectors: string[];
  past_deals: { deal_id: string; name: string; sectors: string }[];
  /** Dictionaries for repeated values; columns below hold indexes into them. */
  dict: { ty: string[]; rg: string[]; st: string[]; cs: string[]; src: string[]; flag: string[]; deal: string[]; outcome: string[]; np: string[] };
  col: {
    /** "" when the key is "e:" + the lowercased email (rebuilt on inflate). */
    k: string[];
    id: string[];
    n: string[];
    co: string[];
    ti: string[];
    ci: string[];
    em: string[];
    ph: string[];
    hm: string[];
    ty: number[];
    rg: number[];
    st: number[];
    cs: number[];
    rs: number[];
    cmin: number[];
    cmax: number[];
    /** Sectors in the person's own order (the first is the main one): one base-36 digit per sector, an index
     *  into `sectors`. "d9" = sectors[13], then sectors[9]. */
    se: string[];
    bu: string[];
    /** Source mask: bit i = dict.src[i]. */
    src: number[];
    /** Last touch, days since epoch; -1 when none. */
    lt: number[];
    /** Sparse: most people have none, so these stay short. [row, index, date] */
    fl: [number, number, string][];
    pa: [number, number, number][];
    ji: [number, number, string][];
    as: [number, number, string][];
    np: [number, number][];
  };
}

class Dict {
  values: string[] = [];
  private index = new Map<string, number>();
  id(v: string | undefined | null): number {
    const s = v ?? "";
    let i = this.index.get(s);
    if (i == null) {
      i = this.values.length;
      this.values.push(s);
      this.index.set(s, i);
    }
    return i;
  }
}

const DAY = 86400000;

export function slimNetwork(net: Network): SlimNetwork {
  const sectors = net.sectors?.length ? [...net.sectors] : [...SECTORS];
  if (sectors.length > 31) throw new Error("slimNetwork: more than 31 sectors");
  const sectorIndex = new Map(sectors.map((s, i) => [s, i]));
  const codeOf = (list: string[] | undefined) => {
    let code = "";
    for (const s of list ?? []) {
      const i = sectorIndex.get(s);
      if (i != null && !code.includes(i.toString(36))) code += i.toString(36);
    }
    return code;
  };
  const d = { ty: new Dict(), rg: new Dict(), st: new Dict(), cs: new Dict(), src: new Dict(), flag: new Dict(), deal: new Dict(), outcome: new Dict(), np: new Dict() };
  const col: SlimNetwork["col"] = {
    k: [], id: [], n: [], co: [], ti: [], ci: [], em: [], ph: [], hm: [], ty: [], rg: [], st: [], cs: [], rs: [], cmin: [], cmax: [],
    se: [], bu: [], src: [], lt: [], fl: [], pa: [], ji: [], as: [], np: [],
  };
  net.contacts.forEach((c, r) => {
    const em = c.em || "";
    col.k.push(c.k && c.k === "e:" + em.toLowerCase() ? "" : c.k || "");
    col.id.push(c.id || "");
    col.n.push(c.n || "");
    col.co.push(c.co || "");
    col.ti.push(c.ti || "");
    col.ci.push(c.ci || "");
    col.em.push(em);
    col.ph.push(c.ph || "");
    col.hm.push(c.hm || "");
    col.ty.push(d.ty.id(c.ty));
    col.rg.push(d.rg.id(c.rg));
    col.st.push(d.st.id(c.st));
    col.cs.push(d.cs.id(c.cs));
    col.rs.push(c.rs || 0);
    col.cmin.push(c.cmin || 0);
    col.cmax.push(c.cmax || 0);
    col.se.push(codeOf(c.se));
    col.bu.push(codeOf(c.bu));
    let sm = 0;
    for (const s of c.src ?? []) sm |= 1 << d.src.id(s);
    col.src.push(sm);
    col.lt.push(Math.max(c.ib?.length ? c.ib[c.ib.length - 1] : -1, c.ob?.length ? c.ob[c.ob.length - 1] : -1));
    for (const f of c.fl ?? []) col.fl.push([r, d.flag.id(f.flag), f.date || ""]);
    for (const p of c.pa ?? []) col.pa.push([r, d.deal.id(p.deal), d.outcome.id(p.outcome)]);
    if (c.ji && sectorIndex.has(c.ji.sector)) col.ji.push([r, sectorIndex.get(c.ji.sector)!, c.ji.date || ""]);
    // Without `as` (no asks in the full log, or a network built before it existed) the Lists engine reads the last
    // few touches, and so does this copy; likewise `np` falls back to the notes, which this copy drops.
    const asked = c.as ?? askedSectorsFromTouches((c.rt ?? []).filter((t) => t[2] === "I").map((t) => ({ date: t[0], summary: t[3] })));
    for (const a of asked) if (sectorIndex.has(a.sector)) col.as.push([r, sectorIndex.get(a.sector)!, a.date || ""]);
    for (const p of saidNoTo(c)) col.np.push([r, d.np.id(p)]);
  });
  return {
    v: 1,
    generated: net.generated,
    epoch: net.epoch,
    count: net.contacts.length,
    sectors,
    past_deals: (net.past_deals ?? []).map((p) => ({ deal_id: p.deal_id, name: p.name, sectors: p.sectors })),
    dict: { ty: d.ty.values, rg: d.rg.values, st: d.st.values, cs: d.cs.values, src: d.src.values, flag: d.flag.values, deal: d.deal.values, outcome: d.outcome.values, np: d.np.values },
    col,
  };
}

export function isSlim(x: unknown): x is SlimNetwork {
  const s = x as SlimNetwork | null;
  return !!s && s.v === 1 && !!s.col && !!s.dict && Array.isArray(s.sectors);
}

type Sparse<T> = Map<number, T[]>;
interface SlimIndex {
  /** Sector bit masks per row, for fast filtering (bit i = sectors[i]). */
  masks: Int32Array;
  fl: Sparse<{ flag: string; date: string | null }>;
  pa: Sparse<{ deal: string; outcome: string }>;
  ji: Map<number, { sector: string; date: string | null }>;
  as: Sparse<{ sector: string; date: string | null }>;
  np: Sparse<string>;
  epochMs: number;
}
const INDEX = new WeakMap<SlimNetwork, SlimIndex>();

function sparseIndex(s: SlimNetwork): SlimIndex {
  let ix = INDEX.get(s);
  if (ix) return ix;
  const { col, dict } = s;
  const push = <T>(m: Sparse<T>, r: number, v: T) => {
    const a = m.get(r);
    if (a) a.push(v);
    else m.set(r, [v]);
  };
  const masks = new Int32Array(s.count);
  for (let r = 0; r < s.count; r++) for (const ch of s.col.se[r] || "") masks[r] |= 1 << parseInt(ch, 36);
  ix = { masks, fl: new Map(), pa: new Map(), ji: new Map(), as: new Map(), np: new Map(), epochMs: Date.parse((s.epoch || "2020-01-01") + "T00:00:00Z") };
  for (const [r, f, date] of col.fl) push(ix.fl, r, { flag: dict.flag[f], date: date || null });
  for (const [r, deal, out] of col.pa) push(ix.pa, r, { deal: dict.deal[deal], outcome: dict.outcome[out] });
  for (const [r, sec, date] of col.ji) ix.ji.set(r, { sector: s.sectors[sec], date: date || null });
  for (const [r, sec, date] of col.as) push(ix.as, r, { sector: s.sectors[sec], date: date || null });
  for (const [r, p] of col.np ?? []) push(ix.np, r, dict.np[p]);
  INDEX.set(s, ix);
  return ix;
}

const NONE: never[] = [];

/** The sectors a code stands for, in the person's own order. */
export function sectorsOfCode(s: SlimNetwork, code: string): string[] {
  if (!code) return NONE;
  const out: string[] = [];
  for (const ch of code) out.push(s.sectors[parseInt(ch, 36)]);
  return out;
}

/** Every row's sectors as a bit mask (bit i = sectors[i]), computed once per copy. */
export function sectorMasks(s: SlimNetwork): Int32Array {
  return sparseIndex(s).masks;
}

/** The mask for a list of sectors (unknown names are ignored). */
export function maskOfSectors(s: SlimNetwork, sectors: string[]): number {
  let m = 0;
  for (const x of sectors) {
    const i = s.sectors.indexOf(x);
    if (i >= 0) m |= 1 << i;
  }
  return m;
}

/** One row back to a contact object. Every object has the same shape, which keeps this fast. */
export function inflateRow(s: SlimNetwork, r: number): SlimContact {
  const { col, dict } = s;
  const ix = sparseIndex(s);
  const em = col.em[r];
  let src: string[] = NONE;
  if (col.src[r]) {
    src = [];
    for (let i = 0; i < dict.src.length; i++) if (col.src[r] & (1 << i)) src.push(dict.src[i]);
  }
  const lt = col.lt?.[r] ?? -1;
  return {
    k: col.k[r] || (em ? "e:" + em.toLowerCase() : ""),
    id: col.id[r],
    n: col.n[r],
    co: col.co[r],
    ti: col.ti[r],
    ty: dict.ty[col.ty[r]] || "Unknown",
    ci: col.ci[r],
    st: dict.st[col.st[r]],
    rg: dict.rg[col.rg[r]],
    se: sectorsOfCode(s, col.se[r]),
    cmin: col.cmin[r],
    cmax: col.cmax[r],
    cs: dict.cs[col.cs[r]],
    rs: col.rs[r],
    em,
    ph: col.ph[r],
    hm: col.hm?.[r] ?? "",
    src,
    fl: ix.fl.get(r) || NONE,
    pa: ix.pa.get(r) || NONE,
    bu: sectorsOfCode(s, col.bu[r]),
    ji: ix.ji.get(r) || null,
    as: ix.as.get(r) || NONE,
    np: ix.np.get(r) || NONE,
    lt: lt >= 0 ? new Date(ix.epochMs + lt * DAY).toISOString().slice(0, 10) : "",
  };
}

/** Back to contact objects (the fields the Lists engine and MCP tools read). Pass rows to inflate only some. */
export function inflateNetwork(s: SlimNetwork, rows?: number[]): { generated: string; epoch: string; sectors: string[]; past_deals: SlimNetwork["past_deals"]; contacts: SlimContact[] } {
  const contacts: SlimContact[] = [];
  if (rows) for (const r of rows) contacts.push(inflateRow(s, r));
  else for (let r = 0; r < s.count; r++) contacts.push(inflateRow(s, r));
  return { generated: s.generated, epoch: s.epoch, sectors: s.sectors, past_deals: s.past_deals, contacts };
}
